import React, {useCallback, useEffect, useState} from 'react';
import {ExternalCallHeader} from '@components/call-card/external/ExternalCallHeader';
import {CallMediaPlayer} from '@components/call-card/CallMediaPlayer';
import {
  getPlayerProps,
  PlayerRef,
  registerPlayerEvents,
  registerSharedAreaEvents,
  registerTranscriptScrollEvents,
} from '@components/call-card/Player.utils';
import {List, Map} from 'immutable';
import {PctDouble, Seconds} from '@store/@types/util';
import {CallSpeakerOverlays} from '@components/call-card/CallSpeakerOverlays';
import {CallSpeakerSection} from '@components/call-card/CallSpeakerSection';
import {
  CallAsset,
  DragStatus,
  Segment,
  Speaker,
  TranscriptBlock,
} from '@store/call-card/CallCard.types';
import {CallCardSidebar} from '@components/call-card/CallCardSidebar';
import {CallCardSource} from '@store/call-card/CallCardSource';
import {Call} from '@store/call-card/Call';
import {TopicFilter} from '@store/topic-keyword/TopicKeyword.types';
import '../CallCard.sass';
import {CallCardVariant} from '@components/call-card/CallCard.types';
import {SharedAreas} from '@components/call-card/SharedHighlights.utils';

interface ExternalCallCardProps {
  call: Call;
  callAsset: CallAsset | undefined;
  segments: List<Segment>;
  speakers: List<Speaker>;
  talkTime: Map<number, Seconds>;
  transcript: List<TranscriptBlock>;
  sharedAreas: SharedAreas;
}

export const ExternalCallCard: React.FC<ExternalCallCardProps> = ({
  call,
  callAsset,
  transcript,
  speakers,
  talkTime,
  segments,
  sharedAreas,
}) => {
  const [playerRef, setPlayerRef] = useState<PlayerRef>(null);
  const [playerProps, setPlayerProps] = useState(getPlayerProps(playerRef, call));
  const [dragStatus, setDragStatus] = useState<DragStatus>({
    isDragging: false,
    startPos: null,
    endPos: null,
  });
  const [autoScrollTranscript, setAutoScrollTranscript] = useState(true);

  useEffect(() => {
    if (!autoScrollTranscript) return;
    return registerTranscriptScrollEvents(playerRef, transcript, Map());
  }, [playerRef, transcript, autoScrollTranscript]);
  useEffect(
    () => registerPlayerEvents(playerRef, call, setPlayerProps),
    [call, playerRef, setPlayerProps]
  );
  useEffect(() => registerSharedAreaEvents(playerRef, sharedAreas), [playerRef, sharedAreas]);

  const setTimePct = useCallback(
    (pct: PctDouble) => {
      if (playerRef) {
        playerRef.currentTime = pct * call.duration;
      }
    },
    [call, playerRef]
  );
  const setTimeSeconds = useCallback(
    time => {
      if (playerRef) {
        playerRef.currentTime = time;
      }
    },
    [playerRef]
  );

  return (
    <div className="new-call-card-root external-call-card">
      <div className="call-card-main-content">
        <ExternalCallHeader call={call} />
        <CallMediaPlayer setPlayerRef={setPlayerRef} callAsset={callAsset} />
        <CallSpeakerOverlays
          sections={List()}
          dragStatus={dragStatus}
          setDragStatus={setDragStatus}
          setNewComment={() => null}
          sharedAreas={sharedAreas}
          {...playerProps}
        />
        <CallSpeakerSection
          speakers={speakers}
          talkTime={talkTime}
          segments={segments}
          setTimePct={setTimePct}
          activeTopics={Map()}
          matches={Map()}
          callerId={call.caller.id}
          dragStatus={dragStatus}
          setDragStatus={setDragStatus}
          {...playerProps}
        />
      </div>
      <CallCardSidebar
        topics={List()}
        matches={Map()}
        activeTopics={Map()}
        callId={call.id}
        keywordSearch={CallCardSource.keywordSearch}
        transcript={transcript}
        speakers={speakers}
        setTimeSeconds={setTimeSeconds}
        topicFilter={TopicFilter.EITHER}
        variant={CallCardVariant.EXTERNAL}
        callState={call.state}
        autoScrollTranscript={autoScrollTranscript}
        setAutoScrollTranscript={setAutoScrollTranscript}
        {...playerProps}
      />
    </div>
  );
};
